import { useTranslation } from 'react-i18next';
import { Loader2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ScanClimbActionsProps {
  onSave: () => void;
  onDelete: () => void;
  saving: boolean;
  deleting: boolean;
  /**
   * Whether the climb is described enough to go up: a grade, and the marks the
   * photo has to carry. A name is not among them — a climb goes up under its
   * grade, its holds and its sector, and is named later if anybody bothers (see
   * Climb#name_required?).
   */
  ready: boolean;
}

/**
 * What is done *with* the climb, at the head of the controls column.
 *
 * In the order they are reached for, which is the reverse of how risky they
 * are: the bin first, out on its own at the left; then the save, at the far end
 * of the row where the eye finishes and where the primary action belongs.
 *
 * They stand above the fields rather than under them because the panel is a
 * narrow column with a long form in it, and a save button at the bottom of that
 * is a save button nobody can see while they are working.
 *
 * **How the climb's photograph is *shown* is not here.** A black-and-white wall
 * with the holds in colour was a switch in this row for a while; it is now the
 * reader's own preference on the member app (`climb-photo-effect.ts`), because
 * a setter cannot answer that for every phone the climb will be read on.
 */
export function ScanClimbActions({
  onSave,
  onDelete,
  saving,
  deleting,
  ready,
}: ScanClimbActionsProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-wrap items-center gap-2 border-b pb-3" data-testid="scan-climb-actions">
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className="text-destructive hover:text-destructive"
        onClick={onDelete}
        disabled={deleting}
      >
        {deleting ? (
          <Loader2 className="size-4 animate-spin" aria-hidden />
        ) : (
          <Trash2 className="size-4" aria-hidden />
        )}
        <span className="sr-only">{t('gymAdmin.scans.deleteColor')}</span>
      </Button>

      <Button type="button" size="sm" className="ml-auto" onClick={onSave} disabled={saving || !ready}>
        {saving && <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />}
        {t('gymAdmin.scans.save')}
      </Button>
    </div>
  );
}
